import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Hammer, Trees, Sparkles } from "lucide-react";
import ProductCard from "@/components/ProductCard";
import CommissionForm from "@/components/CommissionForm";
import { fetchProducts } from "@/lib/api";

const HERO_IMG =
  "https://static.prod-images.emergentagent.com/jobs/2d534211-f673-4bdf-b323-dbc700b856c8/images/6e0a58f9cc5b71082cfb67ad179c95b30bb2f447735f49bd1f0c81abec5a5c37.png";
const LIFESTYLE_IMG = "/lifestyle/in-use.jpg";

export default function HomePage() {
  const [products, setProducts] = useState([]);
  const [assistantOpen, setAssistantOpen] = useState(false);

  useEffect(() => {
    fetchProducts().then(setProducts).catch(() => setProducts([]));
  }, []);

  const featured = products.filter((p) => p.featured).slice(0, 4);
  const gridProducts = featured.length >= 4 ? featured : products.slice(0, 4);

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="relative h-[92vh] min-h-[640px] overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={HERO_IMG}
            alt="Master woodworker at the bench"
            className="w-full h-full object-cover kenburns"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/15 to-black/55" />
        </div>

        <div className="relative h-full max-w-[1400px] mx-auto px-6 md:px-12 flex flex-col justify-end pb-20">
          <div className="max-w-3xl">
            <div className="eyebrow !text-[#F5F2EB]/90 mb-6 fade-up">
              Aotearoa · Est 2020. by hand
            </div>
            <h1
              data-testid="hero-h1"
              className="font-serif text-[#F5F2EB] text-5xl md:text-7xl lg:text-[88px] leading-[0.95] tracking-tight font-normal fade-up delay-1"
            >
              Handcrafted timber,<br />
              <span className="italic font-light">built to last.</span>
            </h1>
            <p className="text-[#F5F2EB]/80 text-lg max-w-xl mt-8 leading-relaxed fade-up delay-2">
              One workshop. One pair of hands. A small collection of kitchen,
              game and statement pieces — and the willingness to build whatever
              you can imagine in timber.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4 fade-up delay-3">
              <Link
                to="/shop"
                data-testid="hero-shop-btn"
                className="group inline-flex items-center gap-3 bg-[#FDFBF7] text-[#2C2421] px-7 py-4 text-sm font-medium tracking-wide hover:bg-[#F5F2EB] transition-colors"
              >
                Shop the collection
                <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Brand story split */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32 grid md:grid-cols-12 gap-12 items-center">
        <div className="md:col-span-5">
          <div className="eyebrow mb-6">Our craft</div>
          <h2 className="font-serif text-4xl md:text-5xl tracking-tight leading-[1.05]">
            Every board <span className="italic">tells the story</span> of the tree it came from.
          </h2>
          <p className="mt-8 text-[#2C2421]/80 leading-relaxed text-lg">
            We work in small batches, with timbers which are predominately
            Rimu. Each piece is hand-planed, sanded to a glassy finish, and
            signed before it leaves the bench. No factories. No shortcuts.
            Just timber, tools, and time.
          </p>
          <Link
            to="/story"
            data-testid="story-link"
            className="inline-flex items-center gap-2 mt-10 link-underline text-sm font-medium tracking-wide"
          >
            Read our story <ArrowRight size={14} />
          </Link>
        </div>

        <div className="md:col-span-7 grid grid-cols-6 grid-rows-6 gap-3 h-[520px]">
          <div className="col-span-4 row-span-4 product-img-wrap">
            <img
              src={LIFESTYLE_IMG}
              alt="Sliders served on a Woodworkers Shed serving board"
              className="product-img w-full h-full object-cover"
            />
          </div>
          <div className="col-span-2 row-span-3 product-img-wrap bg-[#F5F2EB]">
            <img
              src="/lifestyle/bbq-tomahawk.jpg"
              alt="Smoked tomahawk steak resting on a Woodworkers Shed board"
              className="product-img w-full h-full object-cover"
            />
          </div>
          <div className="col-span-2 row-span-3 product-img-wrap bg-[#F5F2EB]">
            <img
              src="/lifestyle/bbq-board.jpg"
              alt="Cherry serving board beside a charcoal grill"
              className="product-img w-full h-full object-cover"
            />
          </div>
          <div className="col-span-4 row-span-2 product-img-wrap">
            <img
              src="/lifestyle/bbq-smoker-deck.jpg"
              alt="Cherry wood serving deck on a custom-built smoker"
              className="product-img w-full h-full object-cover"
            />
          </div>
        </div>
      </section>

      {/* Featured products */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 pb-24">
        <div className="flex items-end justify-between mb-12">
          <div>
            <div className="eyebrow mb-4">The Collection</div>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight">
              Pieces in the workshop
            </h2>
          </div>
          <Link
            to="/shop"
            data-testid="shop-all-link"
            className="hidden md:inline-flex items-center gap-2 link-underline text-sm font-medium"
          >
            View all <ArrowRight size={14} />
          </Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
          {gridProducts.map((p, i) => (
            <ProductCard key={p.id} product={p} index={i} />
          ))}
        </div>
      </section>

      {/* Values strip */}
      <section className="bg-[#F5F2EB] py-20 border-y border-[#E5E0D8]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid md:grid-cols-3 gap-10">
          {[
            { icon: Trees, title: "Sourced with care", body: "Native recycled timber, chosen one slab at a time." },
            { icon: Hammer, title: "Made by one pair of hands", body: "Every piece is hand-built and signed by the maker." },
            { icon: Sparkles, title: "Built to outlive us", body: "Joinery, finishes and design that age into the family." },
          ].map((v, i) => (
            <div key={v.title} className="fade-up" style={{ animationDelay: `${i * 80}ms` }}>
              <v.icon size={22} className="text-[#6F4E37] mb-5" />
              <div className="font-serif text-2xl mb-3">{v.title}</div>
              <p className="text-[#2C2421]/75 leading-relaxed">{v.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Commission CTA */}
      <section className="relative py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={LIFESTYLE_IMG}
            alt="commission"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-[#1C1816]/65" />
        </div>
        <div className="relative max-w-3xl mx-auto px-6 text-center text-[#F5F2EB]">
          <div className="eyebrow !text-[#F5F2EB]/80 mb-5">Bespoke</div>
          <h2 className="font-serif text-4xl md:text-6xl tracking-tight leading-tight">
            Commission a piece <span className="italic">made for you</span>.
          </h2>
          <p className="mt-6 text-[#F5F2EB]/80 leading-relaxed text-lg">
            Tell us your idea on a short form — timber, dimensions, and timeline —
            and we'll quote within 48 hours.
          </p>
          <button
            data-testid="cta-open-assistant"
            onClick={() => setAssistantOpen(true)}
            className="mt-10 inline-flex items-center gap-3 bg-[#FDFBF7] text-[#2C2421] px-7 py-4 text-sm font-medium hover:bg-[#F5F2EB] transition-colors"
          >
            <Sparkles size={14} /> Start your brief
          </button>
        </div>
      </section>

      <CommissionForm open={assistantOpen} onOpenChange={setAssistantOpen} />
    </div>
  );
}
